import DeviceManager, { onPromiseAttrType } from '../utils/device-manager';

export default function initStickyBackground() : void {
    const $stickyBackgroundContainers = document.querySelectorAll<HTMLElement>('.js-sticky-background');

    $stickyBackgroundContainers.forEach($container => {
        new StickyBackground($container);
    });
}

export const StickyBackgroundFakeClickEvent = new CustomEvent('sticky-background-fake-click');

/**
 * Sticky background
 * 
 * Usage:
 * <ul
 *   class="js-sticky-background"
 *   data-sticky-background-color="#fff"    (Optional)
 *   data-sticky-background-radius="10px"   (Optional)
 *   data-sticky-background-height="2px"    (Optional)
 * >
 *     <li><button type="button" class="js-sticky-background-element">Element</button></li>
 * </ul>
 * 
 * If your buttons are managed with something other than the click event,
 * Use the StickyBackgroundFakeClickEvent to tell StickyBackground that the active button has changed
 * $button.dispatchEvent(StickyBackgroundFakeClickEvent);
 */
export class StickyBackground {
    $container: HTMLElement;
    desktopInitialized: boolean;
    containerPaddingLeft: number;
    $elements: HTMLElement[];
    $activeElement: HTMLElement;
    backgroundColor: string;
    borderRadius: string;
    backgroundHeight: string|null;
    $background: HTMLElement;

    constructor($container: HTMLElement) {
        this.$container = $container;
        this.$elements = Array.from(this.$container.querySelectorAll('.js-sticky-background-element'));
        this.desktopInitialized = false;

        if (this.$elements.length === 0) {
            console.error('Container with class ".js-sticky-background" must have elements with class ".js-sticky-background-element".');
            return;
        }

        DeviceManager.addCallback((attr: onPromiseAttrType) => {
            if (DeviceManager.isGreaterThanOrEqual(attr.current, 'md')) {
                if (!this.desktopInitialized) {
                    this.desktopInitialized = true;

                    // Wait before initialization to prevent style mistakes
                    setTimeout(() => {
                        this.init();
                    }, 100);
                }
            }
        });
    }

    /**
     * Initialize StickyBackground
     * 
     * @return {void}
     */
    init() : void {
        this.$activeElement = this.$elements.filter($element => $element.classList.contains('active')).pop() || this.$elements[0];

        this.backgroundColor = this.$container.getAttribute('data-sticky-background-color') || 'var(--color-primary)';
        this.borderRadius = this.$container.getAttribute('data-sticky-background-radius') || '0px';
        this.backgroundHeight = this.$container.getAttribute('data-sticky-background-height') || null;

        this.initializeBackground();

        this.$elements.forEach($element => {
            $element.addEventListener('mouseenter', () => {
                if (this.$activeElement !== $element) {
                    this.$activeElement.classList.remove('active');
                    this.changeFor($element);
                }
            });

            $element.addEventListener('mouseleave', () => {
                if (this.$activeElement !== $element) {
                    this.$activeElement.classList.add('active');
                    this.changeFor(this.$activeElement);
                }
            });

            $element.addEventListener('click', () => {
                if (this.$activeElement !== $element) {
                    this.$activeElement = $element;
                    this.changeFor($element);
                }
            });

            $element.addEventListener('sticky-background-fake-click', () => {
                if (this.$activeElement !== $element) {
                    this.$activeElement = $element;
                    this.changeFor($element);
                }
            });
        });
    }

    /**
     * Create the background element
     * and apply necessary styles
     * 
     * @return {void}
     */
    initializeBackground() : void {
        this.$background = document.createElement('div');
        this.$background.classList.add('sticky-background');
        this.$background.style.position = 'absolute';
        this.$background.style.zIndex = '0';
        this.$background.style.pointerEvents = 'none';
        this.$background.style.backgroundColor = this.backgroundColor;
        this.$background.style.borderRadius = this.borderRadius;
        this.$background.style.transition = '.4s';

        const containerComputedStyle = window.getComputedStyle(this.$container);
        if (containerComputedStyle.position === 'static') {
            this.$container.style.position = 'relative';
        }
        this.containerPaddingLeft = parseFloat(containerComputedStyle.paddingLeft);

        const activeElementComputedStyle = window.getComputedStyle(this.$activeElement);
        const setElementsRelative = activeElementComputedStyle.position === 'static';
        this.$elements.forEach($element => {
            if (setElementsRelative) {
                $element.style.position = 'relative';
            }

            $element.style.zIndex = '1';
        });

        this.$background.style.height = this.backgroundHeight || activeElementComputedStyle.height;

        this.changeFor(this.$activeElement);

        this.$container.appendChild(this.$background);
    }

    /**
     * Change the background position
     * 
     * @param {HTMLElement} $element
     * 
     * @return {void}
     */
    changeFor($element: HTMLElement) : void {
        const elementComputedStyle = window.getComputedStyle($element);

        this.$background.style.width = elementComputedStyle.width;

        const posX = $element.getBoundingClientRect().left - this.$container.getBoundingClientRect().left;
        this.$background.style.left = posX + 'px';
    }
}
